'use client';

/**
 * Intent Widget — Phase 2 canvas for intent mode.
 *
 * @component_role Canvas widget for intent mode (single-query iteration).
 * @business_rule Reads the latest intent_search response from intentStore
 *   and renders the product grid. Same flat product card geometry as
 *   discovery — only the data source changes.
 *
 *   Pareto facet, frontier toggle, and rich product card details are
 *   v2 of this widget. v1 is a clean grid that proves the wiring end-
 *   to-end.
 *
 * @user_outcome Buyer hits Search in the intent composer; the canvas
 *   renders product cards from `intent_search` results. Click a card →
 *   selects in graph-selection-store → copilot details panel updates.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery (intent)
 */

import { Loader2, Package } from 'lucide-react';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import type { CanvasWidgetProps } from '@/lib/registry/types';
import type { CollectionCard, DiscoverySearchResult } from '@/lib/api/types';

export function IntentWidget(_props: CanvasWidgetProps) {
  const response = useIntentStore((s) => s.response);
  const isLoading = useIntentStore((s) => s.isLoading);
  const error = useIntentStore((s) => s.error);
  const selectedProduct = useIntentStore((s) => s.selectedProduct);
  const selectProduct = useIntentStore((s) => s.selectProduct);

  const L = useExplorerLabels('intent');
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  // Loading / error / empty
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">{L.composer_searching ?? 'Searching...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  const products = response?.products?.results ?? [];
  if (!response) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Package className="w-10 h-10 mb-3 opacity-50" />
        <p className="text-sm">{L.history_empty_text ?? 'Describe what you\'re looking for.'}</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Package className="w-10 h-10 mb-3 opacity-50" />
        <p className="text-sm">{L.canvas_no_results ?? 'No products matched.'}</p>
      </div>
    );
  }

  const handleSelect = (product: DiscoverySearchResult) => {
    selectProduct(product);
    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
    });
  };

  return (
    <div className="h-full overflow-auto p-4">
      <div className="text-xs text-muted-foreground mb-3">
        {(L.canvas_results_count ?? '{n} results').replace(
          '{n}',
          String(response.products?.total_count ?? products.length),
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {products.map((p) => (
          <ProductCard
            key={p.semantic_id}
            product={p}
            selected={selectedProduct?.semantic_id === p.semantic_id}
            onSelect={() => handleSelect(p)}
          />
        ))}
      </div>

      <CollectionsRow
        collections={response.collections ?? []}
        onSelect={(c) =>
          selectNode({ id: c.semantic_id, label: c.name, type: 'collection' })
        }
        label={L.canvas_collections_header ?? 'Collections'}
      />
    </div>
  );
}

// ============================================================
// Collections row — shown below the product grid
// ============================================================

function CollectionsRow({
  collections,
  onSelect,
  label,
}: {
  collections: CollectionCard[];
  onSelect: (c: CollectionCard) => void;
  label: string;
}) {
  if (!collections || collections.length === 0) return null;
  return (
    <div className="mt-6">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {collections.map((c) => (
          <div
            key={c.semantic_id}
            onClick={() => onSelect(c)}
            className={cn(
              'group flex flex-col p-2 rounded-lg border border-panel-border bg-card cursor-pointer',
              'hover:border-primary/50 hover:shadow-md transition-all',
            )}
          >
            <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2 relative">
              {c.thumbnail ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={c.thumbnail}
                  alt={c.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-muted-foreground/50" />
                </div>
              )}
            </div>
            <p className="text-xs font-medium line-clamp-2 leading-tight">{c.name}</p>
            <div className="mt-1 flex items-center justify-between gap-1 text-[10px] text-muted-foreground">
              <span>{c.match_count}/{c.total_products}</span>
              {c.match_label && <span className="truncate">{c.match_label}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Product Card — flat card geometry shared with discovery
// ============================================================

function ProductCard({
  product,
  selected,
  onSelect,
}: {
  product: DiscoverySearchResult;
  selected: boolean;
  onSelect: () => void;
}) {
  const props = product.properties || {};
  const price = props.price;
  // Card vocabulary matches intent-grid-widget's LaneCard so the
  // single-query canvas and the grid canvas feel like one system.
  return (
    <div
      onClick={onSelect}
      className={cn(
        'group flex flex-col p-2 rounded-lg border border-panel-border bg-card cursor-pointer',
        'hover:border-primary/50 hover:shadow-md transition-all',
        'text-left',
        selected && 'ring-2 ring-primary border-primary',
      )}
    >
      <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2 relative">
        {props.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={props.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-5 h-5 text-muted-foreground/50" />
          </div>
        )}
      </div>
      <p className="text-xs font-medium line-clamp-2 leading-tight">{product.name}</p>
      <div className="mt-1 flex items-center justify-between gap-1 text-[10px] text-muted-foreground">
        {price != null && <span>${Number(price).toFixed(0)}</span>}
        <span>sim {(product.similarity ?? 0).toFixed(2)}</span>
      </div>
      {props.vendor_name && (
        <p className="text-[10px] text-muted-foreground truncate">{props.vendor_name}</p>
      )}
    </div>
  );
}
